// One floor of the building: hall + elevator shaft, walkways, department pods with desks, props and
// agents. Everything repeated (desks, chairs, props, body parts) is instanced; the floor owns only
// the few geometries whose size depends on its layout (pods, slab, walkways, shaft).
import {
  Box3, Color, CylinderGeometry, Euler, Group, InstancedBufferAttribute, InstancedMesh, Matrix4,
  Mesh, MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, Quaternion, RingGeometry, Sprite,
  TorusGeometry, Vector3, DynamicDrawUsage,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { DIMS, floorLayout, deptColor, lookFor, rng, hashString, floorNumber } from './layout.js';
import { mixHex, lighten } from './palette.js';
import { merge, roundedSlab, roundPlatform, nineSliceAo } from './geometries.js';
import { drawRingText, canvasTexture } from './textures.js';
import { Crowd } from './agents.js';
import { ChickenView } from './chicken-view.js';
import { CatView } from './cat-view.js';

/** The floor's pets (decoration): kind → its view. */
const PET_VIEWS = Object.freeze({ chicken: ChickenView, cat: CatView });

const _m = new Matrix4();
const _q = new Quaternion();
const _e = new Euler();
const _p = new Vector3();
const _s = new Vector3();
const _c = new Color();
const MUGS = ['#F4F1EA', '#E8837A', '#6FAFE6', '#E6C35C', '#2F3A56', '#93C27F'];

/**
 * Floor view: the glass shaft is only seen around the cabin. Its alpha map fades from the hall
 * (v = 0) to nothing at v = 1; v is rescaled so that fade spans SHAFT_FADE_UNITS whatever the shaft
 * height (the building view sets the map's repeat to ~0 and shows the whole column).
 */
export const SHAFT_FADE_UNITS = 3.4;

function fadeUv(geo, height) {
  const uv = geo.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setY(i, uv.getY(i) * (height / SHAFT_FADE_UNITS));
  uv.needsUpdate = true;
}

function compose(x, y, z, rotY = 0, sx = 1, sy = 1, sz = 1) {
  return composeE(x, y, z, 0, rotY, 0, sx, sy, sz);
}

function composeE(x, y, z, rx, ry, rz, sx = 1, sy = 1, sz = 1) {
  _e.set(rx, ry, rz);
  _q.setFromEuler(_e);
  return new Matrix4().compose(_p.set(x, y, z), _q, _s.set(sx, sy, sz));
}

export class FloorView {
  /**
   * @param {{floor:object, depts:Array<{dept:object, agents:object[]}>, index:number, isTop:boolean,
   *   geos:object, mats:object, slabRect?:{x:number,z:number,w:number,d:number}}} opts
   *   slabRect: common building footprint (defaults to this floor's own slab)
   */
  constructor({ floor, depts, index, isTop, geos, mats, slabRect }) {
    this.floor = floor;
    this.id = floor.id;
    this.index = index;
    /** The number shown for this floor (its real order + 1). */
    this.number = floorNumber(floor, index);
    this.isTop = isTop;
    this.geos = geos;
    this.mats = mats;
    this.group = new Group();
    this.group.name = `floor-${floor.id}`;
    this.owned = [];
    this.casters = [];
    this.instanced = [];
    this.layout = floorLayout(depts.map((d) => ({ id: d.dept.id, count: d.agents.length })));
    if (slabRect) this.layout.slab = { ...slabRect };
    this.pods = this.layout.pods.map((p, i) => ({
      ...p, dept: depts[i].dept, agents: depts[i].agents, color: deptColor(depts[i].dept),
    }));
    this.podById = new Map(this.pods.map((p) => [p.deptId, p]));
    this.activity = 0;
    /**
     * The floor's pets, null when off: « La poule de l’étage » (ChickenView), « Le chat de l’étage »
     * (CatView). They follow the sun shadow setting.
     */
    this.pets = { chicken: null, cat: null };
    this.castShadow = true;
    this.floorHover = false;
    this.buildingT = 0;
    this.hoverDept = null;
    const t = mats.tokens;
    this.buildBase(t);
    this.buildPods(t);
    this.buildFurniture(t);
    this.buildProps(t);
    this.buildAgents();
    this.buildBuildingParts(t);
    this.setBuildingT(0);
  }

  own(obj) {
    this.owned.push(obj);
    return obj;
  }

  mesh(geo, mat, { cast = true, receive = true, name } = {}) {
    const m = new Mesh(geo, mat);
    m.castShadow = cast;
    m.receiveShadow = receive;
    if (name) m.name = name;
    if (cast) this.casters.push(m);
    this.group.add(m);
    return m;
  }

  inst(geo, mat, matrices, { colors = null, cast = true, receive = true, name, dynamic = false } = {}) {
    if (!matrices.length) return null;
    const m = new InstancedMesh(geo, mat, matrices.length);
    if (dynamic) m.instanceMatrix.setUsage(DynamicDrawUsage);
    matrices.forEach((mx, i) => m.setMatrixAt(i, mx));
    if (colors) {
      colors.forEach((hex, i) => m.setColorAt(i, _c.set(hex)));
      m.instanceColor.needsUpdate = true;
    }
    m.castShadow = cast;
    m.receiveShadow = receive;
    m.name = name || '';
    m.computeBoundingSphere();
    if (cast) this.casters.push(m);
    this.instanced.push(m);
    this.group.add(m);
    return m;
  }

  buildBase(t) {
    const D = DIMS;
    const { geos, mats } = this;
    // Soft ambient shadows on the ground under the hall and the pods.
    const ao = [compose(0, 0.003, 0, 0, D.hallR * 3.1, 1, D.hallR * 3.1)];
    this.inst(geos.plane, mats.contact, ao, { cast: false, receive: false, name: 'hall-ao' });
    if (this.pods.length) {
      const podAo = merge(this.pods.map((p) => nineSliceAo(p.w - 0.2, p.d - 0.2, 1.0).translate(p.x, 0.004, p.z)));
      this.mesh(this.own(podAo), mats.podShadow, { cast: false, receive: false, name: 'pod-ao' });
    }

    // Hall platform, painted ring text, elevator shaft.
    this.hall = this.mesh(this.own(roundPlatform(D.hallR, D.hallH)), mats.hall, { name: 'hall' });
    this.ringCanvas = drawRingText(null, this.ringTextOpts(t));
    this.ringTex = this.own(canvasTexture(this.ringCanvas));
    this.ringMat = this.own(new MeshBasicMaterial({
      map: this.ringTex, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2,
    }));
    const ringGeo = this.own(new RingGeometry(1.15, D.hallR - 0.2, 128, 1));
    ringGeo.rotateX(-Math.PI / 2);
    const ring = new Mesh(ringGeo, this.ringMat);
    ring.rotation.y = Math.PI / 4;
    ring.position.y = D.hallH + 0.003;
    ring.renderOrder = 1;
    this.group.add(ring);

    const shaftH = this.isTop ? 3.4 : D.floorGap - D.hallH - D.slabH - 0.02;
    this.shaftH = shaftH;
    const glassGeo = this.own(new CylinderGeometry(D.shaftR, D.shaftR, shaftH, 56, 1, true));
    glassGeo.translate(0, shaftH / 2 + D.hallH, 0);
    fadeUv(glassGeo, shaftH);
    this.glass = this.mesh(glassGeo, mats.glass, { cast: false, receive: false, name: 'shaft' });
    this.glass.renderOrder = 3;
    const mullions = [];
    for (let k = 0; k < 6; k++) {
      const a = Math.PI / 4 + (k * Math.PI) / 3;
      const c = new CylinderGeometry(0.032, 0.032, shaftH, 6, 1, true);
      c.translate(Math.cos(a) * D.shaftR, shaftH / 2 + D.hallH, Math.sin(a) * D.shaftR);
      fadeUv(c, shaftH);
      mullions.push(c);
    }
    this.mesh(this.own(merge(mullions)), mats.glassFrame, { cast: false, receive: false, name: 'mullions' });
    const baseRing = this.own(new TorusGeometry(D.shaftR + 0.02, 0.06, 8, 56));
    baseRing.rotateX(Math.PI / 2);
    baseRing.translate(0, D.hallH + 0.04, 0);
    this.mesh(baseRing, mats.frame, { cast: false, name: 'shaft-base' });
    if (this.isTop) {
      const capGeo = this.own(new CylinderGeometry(D.shaftR + 0.12, D.shaftR + 0.12, 0.14, 56));
      capGeo.translate(0, D.hallH + shaftH + 0.07, 0);
      this.cap = this.mesh(capGeo, mats.frame, { cast: false, name: 'shaft-cap' });
    }

    // Walkways between the hall and each pod (one merged mesh), with small edge lights.
    const walks = [];
    const studs = [];
    const studGlow = [];
    for (const w of this.layout.walkways) {
      const g = new RoundedBoxGeometry(w.length, D.walkH, D.walkW, 2, 0.06);
      g.rotateY(-w.angle);
      g.translate(w.x, D.walkH / 2, w.z);
      walks.push(g);
      const ux = Math.cos(w.angle);
      const uz = Math.sin(w.angle);
      const n = Math.max(1, Math.floor(w.length / 1.3));
      for (let i = 0; i < n; i++) {
        const along = -w.length / 2 + 0.55 + (i * (w.length - 1.1)) / Math.max(1, n - 1);
        for (const side of [-1, 1]) {
          const x = w.x + ux * along - uz * side * (D.walkW / 2 - 0.1);
          const z = w.z + uz * along + ux * side * (D.walkW / 2 - 0.1);
          studs.push(compose(x, D.walkH + 0.01, z));
          studGlow.push(compose(x, D.walkH + 0.006, z, 0, 0.9, 1, 0.9));
        }
      }
    }
    if (walks.length) this.mesh(this.own(merge(walks)), mats.walkway, { name: 'walkways' });
    this.inst(geos.stud, mats.lampBulb, studs, { cast: false, receive: false, name: 'walk-lights' });
    this.studGlow = this.inst(geos.plane, mats.glow, studGlow, {
      cast: false, receive: false, name: 'walk-glow', colors: studGlow.map(() => '#000000'),
    });
    // Warm light spilling from the elevator onto the hall (night only; black = invisible).
    this.hallGlow = this.inst(geos.plane, mats.glow, [compose(0, D.hallH + 0.006, 0, 0, 6.2, 1, 6.2)], {
      cast: false, receive: false, name: 'hall-glow', colors: ['#000000'],
    });
  }

  ringTextOpts(t) {
    return {
      name: this.floor.name || this.floor.id,
      label: `étage ${this.number}`,
      color: t.ringText,
      line: t.ringLine,
    };
  }

  buildPods(t) {
    const D = DIMS;
    const tiles = [];
    for (const pod of this.pods) {
      const geo = this.own(new RoundedBoxGeometry(pod.w, D.podH, pod.d, 4, D.podRadius));
      pod.mat = this.own(new MeshStandardMaterial({ roughness: 0.93, metalness: 0 }));
      pod.mesh = this.mesh(geo, pod.mat, { name: `pod-${pod.deptId}` });
      pod.mesh.position.set(pod.x, D.podH / 2, pod.z);
      pod.mesh.userData.deptId = pod.deptId;
      // Faint floor tiles on the pod top.
      const tw = pod.w - 0.7;
      const td = pod.d - 0.7;
      const tg = new PlaneGeometry(tw, td);
      const uv = tg.attributes.uv;
      for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * tw * 0.9, uv.getY(i) * td * 0.9);
      tg.rotateX(-Math.PI / 2);
      tg.translate(pod.x, D.podH + 0.002, pod.z);
      tiles.push(tg);
      // Rug under the team (or under the lead when alone).
      const g = pod.grid;
      const rw = g.block ? g.block.w + 0.35 : 2.6;
      const rd = g.block ? g.block.d - 0.25 : 3.0;
      const rx = g.block ? g.block.x : g.lead ? g.lead.x - 0.35 : 0;
      const rz = g.block ? g.block.z : 0;
      const rugGeo = this.own(new PlaneGeometry(rw, rd));
      rugGeo.rotateX(-Math.PI / 2);
      pod.rugMat = this.own(new MeshStandardMaterial({
        map: this.mats.tx.rug, roughness: 1, alphaTest: 0.5, polygonOffset: true, polygonOffsetFactor: -1,
      }));
      const rug = this.mesh(rugGeo, pod.rugMat, { cast: false, name: `rug-${pod.deptId}` });
      rug.position.set(pod.x + rx, D.podH + 0.004, pod.z + rz);
    }
    if (tiles.length) {
      const m = this.mesh(this.own(merge(tiles)), this.mats.tiles, { cast: false, receive: false, name: 'tiles' });
      m.renderOrder = 1;
    }
    this.applyPodColors(t);
  }

  applyPodColors(t) {
    for (const pod of this.pods) {
      pod.mat.color.set(mixHex(t.stone, pod.color, t.podTint));
      pod.mat.emissive.set(pod.color);
      // Night: a faint glow of the department colour keeps every zone readable; hover adds to it.
      const base = t.podGlow || 0;
      pod.mat.emissiveIntensity = base + (pod.deptId === this.hoverDept ? (t.name === 'dark' ? 0.12 : 0.1) : 0);
      pod.rugMat.color.set(t.name === 'dark' ? mixHex(pod.color, t.stone, t.rugMix) : mixHex(pod.color, '#FFFFFF', t.rugMix));
    }
  }

  buildFurniture(t) {
    const D = DIMS;
    const H = D.deskH;
    const { geos, mats } = this;
    const tops = [];
    const legs = [];
    const monitors = [];
    const keyboards = [];
    const chairs = [];
    const contacts = [];
    const mugs = [];
    const mugColors = [];
    const papers = [];
    const smallPots = [];
    const smallLeaves = [];
    const stars = [];
    this.deskSlots = [];
    for (const pod of this.pods) {
      pod.desks = [];
      pod.grid.desks.forEach((desk, i) => {
        const agent = pod.agents[i] || null;
        const x = pod.x + desk.x;
        const z = pod.z + desk.z;
        const md = compose(x, D.podH, z, desk.rot);
        tops.push(md.clone().multiply(new Matrix4().makeScale(desk.w, 1, desk.dd)));
        legs.push(md.clone().multiply(new Matrix4().makeScale(desk.w, 1, desk.dd)));
        monitors.push(md);
        keyboards.push(md);
        const seat = { x: pod.x + desk.seatX, z: pod.z + desk.seatZ };
        chairs.push(compose(seat.x, D.podH, seat.z, desk.rot));
        const midX = (x + seat.x) / 2;
        const midZ = (z + seat.z) / 2;
        contacts.push(compose(midX, D.podH + 0.003, midZ, desk.rot, desk.w + 0.6, 1, desk.dd + 1.5));
        const slot = { pod, desk, agent, x, z, seat, matrix: md, index: this.deskSlots.length };
        this.deskSlots.push(slot);
        pod.desks.push(slot);
        const look = agent ? lookFor(agent) : null;
        const prop = look ? look.deskProp : 2;
        const local = (lx, lz, ry = 0) => md.clone().multiply(compose(lx * (desk.w / D.deskW), H, lz, ry));
        if (prop === 1 || desk.lead) {
          mugs.push(local(-0.52, -0.12));
          mugColors.push(MUGS[(look ? look.seed : i) % MUGS.length]);
        }
        if (prop === 2 || desk.lead) papers.push(local(0.5, 0.06, 0.25));
        if (prop === 3) {
          smallPots.push(local(-0.56, 0.22));
          smallLeaves.push(local(-0.56, 0.22));
        }
        if (desk.lead) stars.push({ x: 0.62 * (desk.w / 2), z: 0.24, md });
      });
    }
    const deskOpts = { name: 'desk-tops' };
    this.inst(geos.deskTop, mats.wood, tops, deskOpts);
    this.inst(geos.deskLegs, mats.deskLeg, legs, { name: 'desk-legs' });
    this.inst(geos.monitor, mats.monitor, monitors, { name: 'monitors' });
    this.inst(geos.keyboard, mats.keyboard, keyboards, { name: 'keyboards', cast: false });
    this.chairs = this.inst(geos.chair, mats.chair, chairs, { name: 'chairs', dynamic: true });
    this.chairBase = chairs;
    this.inst(geos.plane, mats.contact, contacts, { cast: false, receive: false, name: 'desk-ao' });
    this.inst(geos.mug, mats.tint, mugs, { colors: mugColors, name: 'mugs' });
    this.inst(geos.papers, mats.tint, papers, { colors: papers.map(() => '#F7F4EE'), name: 'papers', cast: false });
    this.inst(geos.smallPot, mats.pot, smallPots, { name: 'desk-pots' });
    this.deskLeaves = this.inst(geos.smallLeaves, mats.leaves, smallLeaves, {
      colors: smallLeaves.map(() => t.leaves), name: 'desk-leaves',
    });
    this.starSlots = stars;
    this.stars = this.inst(geos.star, mats.star, stars.map((s) => s.md), { name: 'lead-stars', dynamic: true });
    this.updateStars(0);
    // The stars float above their desks: bound them where they really are (frustum culling).
    this.stars?.computeBoundingSphere();

    // Screens: one instanced mesh with per-instance "on" level, scroll phase and tint.
    const n = this.deskSlots.length;
    this.screenOn = new Float32Array(n);
    this.screenTint = new Float32Array(n * 3);
    const phase = new Float32Array(n);
    this.deskSlots.forEach((s, i) => {
      phase[i] = (hashString(`${this.id}:${i}`) % 1000) / 1000;
      _c.set(lighten(s.pod.color, 0.25));
      this.screenTint.set([_c.r, _c.g, _c.b], i * 3);
    });
    this.screenOnAttr = new InstancedBufferAttribute(this.screenOn, 1);
    this.screenOnAttr.setUsage(DynamicDrawUsage);
    this.screenTintAttr = new InstancedBufferAttribute(this.screenTint, 3);
    this.screenTintAttr.setUsage(DynamicDrawUsage);
    this.ownedScreenGeo = this.own(geos.screen.clone());
    this.ownedScreenGeo.setAttribute('aOn', this.screenOnAttr);
    this.ownedScreenGeo.setAttribute('aPhase', new InstancedBufferAttribute(phase, 1));
    this.ownedScreenGeo.setAttribute('aTint', this.screenTintAttr);
    this.screens = this.inst(this.ownedScreenGeo, mats.screen, monitors, { cast: false, receive: false, name: 'screens' });
    // Light pools cast by the screens on the desks (additive; black = invisible).
    this.screenGlow = this.inst(geos.screenGlow, mats.glow, monitors, {
      cast: false, receive: false, name: 'screen-glow', colors: monitors.map(() => '#000000'), dynamic: true,
    });
  }

  buildProps(t) {
    const D = DIMS;
    const { geos, mats } = this;
    const pots = [];
    const foliage = [];
    const foliageColors = [];
    const poles = [];
    const blobs = [];
    const lampGlow = [];
    const shelves = [];
    const books = [];
    const bookColors = [];
    this.lampHalos = [];
    this.bookSlots = [];
    this.foliageSlots = [];
    for (const pod of this.pods) {
      const r = rng(hashString(pod.deptId));
      const hw = pod.w / 2;
      const hd = pod.d / 2;
      const plantAt = [[hw - 0.62, -hd + 0.62], [-hw + 0.62, hd - 0.62]];
      for (const [px, pz] of plantAt) {
        const s = 0.9 + r() * 0.3;
        const m = compose(pod.x + px, D.podH, pod.z + pz, r() * Math.PI * 2, s, s, s);
        pots.push(m);
        foliage.push(m);
        const dark = r() < 0.5;
        foliageColors.push(dark ? t.leavesDark : t.leaves);
        this.foliageSlots.push(dark);
        blobs.push(compose(pod.x + px, D.podH + 0.003, pod.z + pz, 0, 1.1, 1, 1.1));
      }
      // Floor lamp in the far corner.
      const lx = pod.x - hw + 0.55;
      const lz = pod.z - hd + 0.55;
      poles.push(compose(lx, D.podH, lz));
      blobs.push(compose(lx, D.podH + 0.003, lz, 0, 0.7, 1, 0.7));
      lampGlow.push(compose(lx + 0.5, D.podH + 0.005, lz + 0.5, 0, 5, 1, 5));
      const halo = new Sprite(mats.lampHalo);
      halo.position.set(lx, D.podH + 1.56, lz);
      halo.scale.setScalar(1.6);
      halo.renderOrder = 4;
      this.group.add(halo);
      this.lampHalos.push(halo);
      // Bookshelf against the far edge, behind the team.
      const g = pod.grid;
      const sx = pod.x + (g.block ? g.block.x : 0.6);
      const sz = pod.z - hd + 0.3;
      shelves.push(compose(sx, D.podH, sz));
      blobs.push(compose(sx, D.podH + 0.003, sz + 0.05, 0, 2.2, 1, 0.9));
      for (const baseY of [0.05, 0.47, 0.9]) {
        let x = -0.74;
        while (x < 0.7) {
          const w = 0.05 + r() * 0.07;
          if (r() < 0.14) { x += w + 0.04; continue; }
          const h = 0.24 + r() * 0.13;
          const lean = r() < 0.12 ? -0.16 : 0;
          books.push(composeE(sx + x + w / 2, D.podH + baseY, sz + 0.02, 0, 0, lean, w, h, 0.25));
          const ci = Math.floor(r() * t.books.length);
          bookColors.push(t.books[ci]);
          this.bookSlots.push(ci);
          x += w + 0.008;
        }
      }
    }
    // Two planters on the hall.
    for (const a of [Math.PI * 0.25 + Math.PI, Math.PI * 0.25 + Math.PI / 2]) {
      const x = Math.cos(a) * (D.hallR - 0.55);
      const z = Math.sin(a) * (D.hallR - 0.55);
      const m = compose(x, D.hallH, z, a, 0.85, 0.85, 0.85);
      pots.push(m);
      foliage.push(m);
      foliageColors.push(t.leaves);
      this.foliageSlots.push(false);
      blobs.push(compose(x, D.hallH + 0.003, z, 0, 1, 1, 1));
    }
    this.inst(geos.pot, mats.pot, pots, { name: 'pots' });
    this.foliage = this.inst(geos.foliage, mats.leaves, foliage, { colors: foliageColors, name: 'foliage' });
    this.inst(geos.lampPole, mats.lampMetal, poles, { name: 'lamp-poles' });
    this.inst(geos.lampShade, mats.lampShade, poles, { name: 'lamp-shades' });
    this.lampGlow = this.inst(geos.plane, mats.glow, lampGlow, {
      cast: false, receive: false, name: 'lamp-glow', colors: lampGlow.map(() => '#000000'),
    });
    this.inst(geos.shelf, mats.shelf, shelves, { name: 'shelves' });
    this.books = this.inst(geos.book, mats.tint, books, { colors: bookColors, name: 'books' });
    this.inst(geos.plane, mats.contact, blobs, { cast: false, receive: false, name: 'props-ao' });
    this.applyGlowColors(t);
  }

  buildAgents() {
    const entries = [];
    for (const slot of this.deskSlots) {
      if (!slot.agent) continue;
      entries.push({
        agent: slot.agent, look: lookFor(slot.agent), seatX: slot.seat.x, seatZ: slot.seat.z,
        rot: slot.desk.rot, podTop: DIMS.podH, deskIndex: slot.index, deptId: slot.pod.deptId,
        deptColor: slot.pod.color,
      });
    }
    this.crowd = new Crowd({ entries, geos: this.geos, mats: this.mats, parent: this.group });
    for (const m of Object.values(this.crowd.meshes)) this.casters.push(m);
    this.rigBySlot = new Map();
    for (const rig of this.crowd.rigs) this.rigBySlot.set(rig.info.deskIndex, rig);
  }

  buildBuildingParts(t) {
    const D = DIMS;
    const s = this.layout.slab;
    const slabGeo = this.own(roundedSlab(s.w, s.d, D.slabH, D.slabRadius, 0.12));
    slabGeo.translate(s.x, -D.slabH, s.z);
    this.slab = this.mesh(slabGeo, this.mats.slab, { cast: false, receive: true, name: 'slab' });
    // Soft shadow on the ground under the building (lowest floor only).
    this.slabShadowMat = this.own(new MeshBasicMaterial({
      map: this.mats.tx.aoRect, transparent: true, depthWrite: false, toneMapped: false, opacity: 0,
    }));
    const slabShadowGeo = this.own(nineSliceAo(s.w, s.d, 2.2));
    slabShadowGeo.translate(s.x + 0.5, -D.slabH - 0.02, s.z + 0.9);
    this.slabShadow = this.mesh(slabShadowGeo, this.slabShadowMat, { cast: false, receive: false, name: 'slab-shadow' });
    this.slabShadow.renderOrder = -1;
    const bandGeo = this.own(roundedSlab(s.w + 0.12, s.d + 0.12, 0.13, D.slabRadius + 0.06, 0.04));
    bandGeo.translate(s.x, -0.3, s.z);
    this.bandMat = this.own(new MeshBasicMaterial({ transparent: true, opacity: 0, toneMapped: false }));
    this.band = this.mesh(bandGeo, this.bandMat, { cast: false, receive: false, name: 'slab-band' });
    this.pillarBase = [];
    if (!this.isTop) {
      const h = D.floorGap - D.slabH;
      for (const [sx, sz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
        this.pillarBase.push({ x: s.x + sx * (s.w / 2 - 1.1), z: s.z + sz * (s.d / 2 - 1.1), h });
      }
      this.pillars = this.inst(this.geos.pillar, this.mats.pillar, this.pillarBase.map(() => new Matrix4()), {
        cast: false, name: 'pillars', dynamic: true,
      });
    }
    this.applyBandColor(t);
  }

  applyBandColor(t) {
    const k = this.floorHover ? 1 : Math.min(1, this.activity);
    this.bandMat.color.set(mixHex(t.slabEdgeIdle, t.slabEdgeGlow, k));
  }

  applyGlowColors(t) {
    const lamp = _c.set(t.lampGlow).multiplyScalar(t.haloStrength * 0.62);
    if (this.lampGlow) {
      for (let i = 0; i < this.lampGlow.count; i++) this.lampGlow.setColorAt(i, lamp);
      this.lampGlow.instanceColor.needsUpdate = true;
    }
    if (this.hallGlow) {
      this.hallGlow.setColorAt(0, _c.set(t.cabinLight).multiplyScalar(t.haloStrength * 0.5));
      this.hallGlow.instanceColor.needsUpdate = true;
    }
    const stud = _c.set(t.lampGlow).multiplyScalar(t.haloStrength * 0.22);
    if (this.studGlow) {
      for (let i = 0; i < this.studGlow.count; i++) this.studGlow.setColorAt(i, stud);
      this.studGlow.instanceColor.needsUpdate = true;
    }
    const night = t.haloStrength > 0;
    for (const h of this.lampHalos) h.visible = night;
    for (const m of [this.lampGlow, this.studGlow, this.hallGlow]) if (m) m.visible = night;
  }

  /** Re-colours everything that depends on the theme (shared materials are handled elsewhere). */
  applyTheme(t) {
    this.applyPodColors(t);
    drawRingText(this.ringCanvas, this.ringTextOpts(t));
    this.ringTex.needsUpdate = true;
    if (this.foliage) {
      this.foliageSlots.forEach((dark, i) => this.foliage.setColorAt(i, _c.set(dark ? t.leavesDark : t.leaves)));
      this.foliage.instanceColor.needsUpdate = true;
    }
    if (this.deskLeaves) {
      for (let i = 0; i < this.deskLeaves.count; i++) this.deskLeaves.setColorAt(i, _c.set(t.leaves));
      this.deskLeaves.instanceColor.needsUpdate = true;
    }
    if (this.books) {
      this.bookSlots.forEach((ci, i) => this.books.setColorAt(i, _c.set(t.books[ci % t.books.length])));
      this.books.instanceColor.needsUpdate = true;
    }
    this.applyGlowColors(t);
    this.applyBandColor(t);
    this.crowd.applyTheme(t);
  }

  /** 0 = floor view (no slab, no pillars), 1 = building view. */
  setBuildingT(v) {
    this.buildingT = v;
    this.slab.visible = v > 0.001;
    this.slabShadow.visible = this.index === 0 && v > 0.001;
    this.slabShadowMat.color.set(this.mats.tokens.contact);
    this.slabShadowMat.opacity = v * this.mats.tokens.contactOpacity * 0.9;
    this.band.visible = v > 0.001;
    this.bandMat.opacity = v;
    if (this.cap) this.cap.visible = v > 0.5;
    if (this.pillars) {
      this.pillars.visible = v > 0.001;
      this.pillarBase.forEach((p, i) => this.pillars.setMatrixAt(i, compose(p.x, 0, p.z, 0, 1, Math.max(0.001, p.h * v), 1)));
      this.pillars.instanceMatrix.needsUpdate = true;
      // The pillars stand at the plate corners, far from where they were created: keep their
      // bounds current or frustum culling hides them when the floor centre leaves the view.
      this.pillars.computeBoundingSphere();
    }
  }

  /** Activity 0..1 (building view edge glow). */
  setActivity(a) {
    this.activity = a;
    this.applyBandColor(this.mats.tokens);
  }

  /** Building view: the pointer is over this floor (its edge band lights up fully). */
  setFloorHover(on) {
    if (this.floorHover === !!on) return;
    this.floorHover = !!on;
    this.applyBandColor(this.mats.tokens);
  }

  setHoverDept(deptId) {
    if (this.hoverDept === deptId) return;
    this.hoverDept = deptId;
    this.applyPodColors(this.mats.tokens);
  }

  setCastShadow(on) {
    this.castShadow = !!on;
    for (const m of this.casters) m.castShadow = on;
    for (const pet of Object.values(this.pets)) pet?.setCastShadow(this.castShadow);
  }

  /** « La poule de l’étage » (ChickenView), null when off. */
  get chicken() {
    return this.pets.chicken;
  }

  /** « Le chat de l’étage » (CatView), null when off. */
  get cat() {
    return this.pets.cat;
  }

  /** Shows or removes the floor's chicken (setPet). */
  setChicken(on, now = 0, at = null) {
    return this.setPet('chicken', on, now, at);
  }

  /** Shows or removes the floor's cat (setPet). */
  setCat(on, now = 0, at = null) {
    return this.setPet('cat', on, now, at);
  }

  /**
   * Shows or removes one of the floor's pets ('chicken' | 'cat'). A pet is created once and kept
   * with the floor until it is switched off or the floor is disposed.
   * @param {string} kind
   * @param {boolean} on
   * @param {number} [now] wall clock (s): its first rest ends a moment after it
   * @param {{x:number, z:number, yaw?:number}|null} [at] where it stood before the floor was rebuilt
   * @returns {boolean} true when something changed
   */
  setPet(kind, on, now = 0, at = null) {
    const View = PET_VIEWS[kind];
    if (!View) return false;
    const pet = this.pets[kind];
    if (!on) {
      if (!pet) return false;
      pet.dispose();
      this.pets[kind] = null;
      return true;
    }
    if (pet) return false;
    const view = new View({
      geos: this.geos, mats: this.mats, layout: this.layout, parent: this.group, seed: hashString(`${kind}:${this.id}`), now, at,
    });
    view.setCastShadow(this.castShadow);
    this.pets[kind] = view;
    return true;
  }

  updateStars(time) {
    if (!this.stars) return;
    const H = DIMS.deskH;
    this.starSlots.forEach((s, i) => {
      const m = s.md.clone().multiply(compose(s.x, H + 0.3 + Math.sin(time * 1.6 + i) * 0.03, s.z, time * 0.8));
      this.stars.setMatrixAt(i, m);
    });
    this.stars.instanceMatrix.needsUpdate = true;
  }

  /**
   * Per-frame update. Returns true when something beyond idle breathing is animating.
   * @param {number} dt
   * @param {number} time
   * @param {{reducedMotion:boolean, emit:Function, bubbleScale:number, idleAnim:boolean}} ctx
   */
  update(dt, time, ctx) {
    const active = this.crowd.update(dt, time, ctx);
    if (!ctx.reducedMotion) this.updateStars(time);
    // Screens, their light pools and chairs follow the rigs.
    const t = this.mats.tokens;
    const glow = _c.set(t.screenGlow);
    let chairsDirty = false;
    for (const [slotIndex, rig] of this.rigBySlot) {
      const on = rig.screenOn;
      if (Math.abs(this.screenOn[slotIndex] - on) > 0.002) {
        this.screenOn[slotIndex] = on;
        this.screenOnAttr.needsUpdate = true;
        const k = Math.max(on, t.screenIdle) * t.screenGlowStrength;
        this.screenGlow.setColorAt(slotIndex, _c.copy(glow).multiplyScalar(k));
        this.screenGlow.instanceColor.needsUpdate = true;
      }
      const wantTint = rig.status === 'error' ? '#FF7B72' : null;
      if (wantTint !== rig._tint) {
        rig._tint = wantTint;
        _c.set(wantTint || lighten(rig.info.deptColor, 0.25));
        this.screenTint.set([_c.r, _c.g, _c.b], slotIndex * 3);
        this.screenTintAttr.needsUpdate = true;
      }
      if (Math.abs((rig._chair ?? 0) - rig.chairPush) > 0.002) {
        rig._chair = rig.chairPush;
        const base = this.chairBase[slotIndex];
        this.chairs.setMatrixAt(slotIndex, base.clone().multiply(_m.makeTranslation(0, 0, -rig.chairPush)));
        chairsDirty = true;
      }
    }
    if (chairsDirty) this.chairs.instanceMatrix.needsUpdate = true;
    return active;
  }

  /** Re-applies screen glow colours after a theme change. */
  refreshScreens() {
    // A floor without desks (no department, or departments without agents) has no screens.
    if (!this.screenGlow) return;
    const t = this.mats.tokens;
    for (let i = 0; i < this.deskSlots.length; i++) {
      const k = Math.max(this.screenOn[i], t.screenIdle) * t.screenGlowStrength;
      this.screenGlow.setColorAt(i, _c.set(t.screenGlow).multiplyScalar(k));
    }
    this.screenGlow.instanceColor.needsUpdate = true;
  }

  /** Floor-local box of the whole floor (for camera fitting). */
  bounds(out = new Box3()) {
    const b = this.layout.bounds;
    const y = this.group.position.y;
    return out.set(new Vector3(b.minX, y, b.minZ), new Vector3(b.maxX, y + 2.2, b.maxZ));
  }

  /** World box of the slab and what stands on it (building view picking). */
  slabBox(out = new Box3()) {
    const s = this.layout.slab;
    const y = this.group.position.y;
    return out.set(new Vector3(s.x - s.w / 2, y - DIMS.slabH, s.z - s.d / 2), new Vector3(s.x + s.w / 2, y + 2, s.z + s.d / 2));
  }

  /** World box of a pod (with some height so people count). */
  podBox(pod, out = new Box3()) {
    const y = this.group.position.y;
    return out.set(new Vector3(pod.x - pod.w / 2, y, pod.z - pod.d / 2), new Vector3(pod.x + pod.w / 2, y + DIMS.podH, pod.z + pod.d / 2));
  }

  /** Releases everything this floor created. */
  dispose() {
    for (const kind of Object.keys(this.pets)) this.setPet(kind, false);
    this.crowd.dispose();
    for (const m of this.instanced) m.dispose();
    for (const o of this.owned) o.dispose?.();
    this.group.clear();
  }
}
